<template>
  <FlashNotifications ref="flash" />

  <div class="flex flex-col h-app-screen pb-14 md:pb-0 bg-gray-50 dark:bg-[#0d1117]">
    <!-- Top bar: back, the name, and whether it is saved -->
    <header class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      <div class="flex items-center gap-2 px-3 sm:px-5 h-12">
        <RouterLink to="/cv_builder" data-turbo-prefetch="false" aria-label="Back to all resumes"
          class="w-8 h-8 -ml-1 inline-flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>
        </RouterLink>

        <template v-if="store.resume">
          <input :value="store.name" @input="store.setName($event.target.value)" @blur="restoreName" type="text" maxlength="120" aria-label="Resume title"
            class="min-w-0 w-48 sm:w-72 h-8 px-2 -mx-1 text-sm font-semibold text-gray-900 dark:text-white bg-transparent rounded-md border border-transparent hover:border-gray-200 dark:hover:border-gray-700 focus:border-blue-400 focus:bg-white dark:focus:bg-[#0d1117] focus:outline-none focus:ring-2 focus:ring-blue-500/30">
          <span v-if="store.resume.is_default" class="hidden sm:inline text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">Default</span>

          <div class="ml-auto flex items-center gap-2 sm:gap-3">
            <p class="text-xs flex items-center gap-1.5" :class="STATUS[store.saveState].tone" role="status" aria-live="polite">
              <svg v-if="store.saveState === 'saving'" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"/></svg>
              <svg v-else-if="store.saveState === 'saved'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
              {{ STATUS[store.saveState].text }}
            </p>
            <button v-if="store.saveState === 'error'" type="button" @click="store.save()" class="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline">Retry</button>

            <HelpLink path="job-tracker/create-your-default-resume#sections" label="Guide" class="hidden lg:inline-flex" />
            <IconButton :icon="ICONS.json" label="Edit as JSON" :tone="jsonOpen ? 'active' : 'default'" :pressed="jsonOpen" :hide-tip="jsonOpen" @click="jsonOpen = !jsonOpen" />
            <IconButton :icon="ICONS.sparkles" label="Fill with sample data" @click="askSample" />

            <IconButton :icon="ICONS.download" label="Download PDF" tone="primary" align="right" :disabled="printing" @click="downloadPdf" />
          </div>
        </template>
      </div>

      <!-- Small screens show the form or the preview, not both -->
      <div v-if="store.resume" class="xl:hidden flex border-t border-gray-100 dark:border-gray-800" role="tablist">
        <button v-for="item in PANES" :key="item.id" type="button" role="tab" :aria-selected="pane === item.id" @click="pane = item.id"
          class="flex-1 h-9 text-[13px] font-semibold border-b-2 transition-colors"
          :class="pane === item.id ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400'">{{ item.label }}</button>
      </div>
    </header>

    <!-- A resume AI wrote says so here, with how it was made -->
    <ResumeAiInfo v-if="store.resume?.ai_generated" :resume="store.resume" />

    <!-- Loading -->
    <div v-if="store.loading" class="flex-1 min-h-0 flex animate-pulse" aria-hidden="true">
      <div class="w-full xl:w-[680px] xl:flex-shrink-0 flex flex-col md:flex-row min-h-0">
        <!-- The section icons, with a line after each, as the real rail has -->
        <div class="flex md:flex-col items-center gap-1 p-2 md:px-0 md:py-2 md:w-14 md:flex-shrink-0 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128]">
          <template v-for="n in 10" :key="n">
            <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800"></div>
            <div class="flex-shrink-0 bg-gray-200 dark:bg-gray-700 w-px h-6 mx-0.5 md:w-full md:h-px md:mx-0"></div>
          </template>
          <div class="flex-shrink-0 w-10 h-10 rounded-lg border border-dashed border-gray-200 dark:border-gray-700"></div>
        </div>

        <!-- Personal details: the heading, the photo row, then the fields -->
        <div class="flex-1 min-w-0 p-4 sm:p-5">
          <div class="max-w-2xl">
            <div class="h-5 w-40 rounded bg-gray-200 dark:bg-gray-700"></div>
            <div class="h-3.5 w-72 max-w-full rounded bg-gray-100 dark:bg-gray-800 mt-2 mb-4"></div>
            <div class="flex items-center gap-4 pb-4 mb-3 border-b border-gray-100 dark:border-gray-800">
              <div class="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
              <div class="flex-1 space-y-2">
                <div class="h-3.5 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div class="h-3 w-56 max-w-full rounded bg-gray-100 dark:bg-gray-800"></div>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div v-for="n in 8" :key="n" class="space-y-1.5">
                <div class="h-3 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div class="h-9 rounded-lg bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- The sheet -->
      <div class="hidden xl:block flex-1 min-w-0 bg-gray-100 dark:bg-[#0a0d12] p-6">
        <div class="max-w-[820px] mx-auto">
          <div class="h-3 w-40 rounded bg-gray-200 dark:bg-gray-700 mb-2.5"></div>
          <div class="aspect-[210/297] rounded bg-white dark:bg-[#1C2128] shadow p-[6%] space-y-4">
            <div class="flex items-center justify-between">
              <div class="space-y-2 flex-1"><div class="h-6 w-1/2 rounded bg-gray-200 dark:bg-gray-700"></div><div class="h-3 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div></div>
              <div class="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700"></div>
            </div>
            <div v-for="n in 3" :key="n" class="space-y-2 pt-2">
              <div class="h-3 w-1/4 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div class="h-2.5 w-full rounded bg-gray-100 dark:bg-gray-800"></div>
              <div class="h-2.5 w-11/12 rounded bg-gray-100 dark:bg-gray-800"></div>
              <div class="h-2.5 w-4/5 rounded bg-gray-100 dark:bg-gray-800"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Not found, or could not load -->
    <div v-else-if="!store.resume" class="flex-1 flex flex-col items-center justify-center text-center px-4 py-24">
      <h2 class="text-base font-semibold text-gray-900 dark:text-white">{{ store.notFound ? 'Resume not found' : 'Something went wrong' }}</h2>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-5">{{ store.notFound ? 'It may have been deleted.' : store.error }}</p>
      <div class="flex gap-2">
        <RouterLink to="/cv_builder" data-turbo-prefetch="false" class="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700">All resumes</RouterLink>
        <button v-if="!store.notFound" type="button" @click="store.load(route.params.id)" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Try again</button>
      </div>
    </div>

    <!-- The editor: the sections, the form for the one picked, and the preview -->
    <div v-else class="relative flex-1 min-h-0 flex overflow-hidden">
      <div class="flex flex-col md:flex-row min-h-0 w-full xl:w-[680px] xl:flex-shrink-0 xl:border-r border-gray-200 dark:border-gray-700" :class="pane === 'edit' ? '' : 'hidden xl:flex'">
        <SectionRail :content="store.content" :active="active" :can-add="store.content.custom.length < MAX_CUSTOM" @select="active = $event" @add="addCustom" />

        <div class="flex-1 min-w-0 min-h-0 overflow-y-auto">
          <div class="p-4 sm:p-5 max-w-2xl">
            <div v-if="store.beforeSample" role="status" class="flex flex-wrap items-center gap-x-3 gap-y-1 mb-4 px-3 py-2 rounded-lg border border-blue-100 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 text-[13px] text-blue-800 dark:text-blue-200">
              <span class="flex-1 min-w-0">Sample data added. Replace it with your own details.</span>
              <button type="button" @click="store.undoSample()" class="font-semibold underline underline-offset-2 hover:no-underline">Undo</button>
              <button type="button" @click="store.dismissSample()" class="font-semibold text-blue-600 dark:text-blue-300 hover:underline">Keep it</button>
            </div>

            <div class="mb-4">
              <h2 class="text-base font-bold text-gray-900 dark:text-white">{{ heading }}</h2>
              <p class="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">{{ HINTS[active] || 'Your own section. It shows on the resume once it has an entry.' }}</p>
            </div>

            <BasicsSection v-if="active === 'basics'" :basics="store.content.basics" :settings="store.content.settings"
              :resume-id="store.resume.id" :picture-url="store.resume.picture_url" @picture-updated="store.setPicture($event.picture_url)"
              @picture-error="flash?.show($event, 'alert')" />

            <FormField v-else-if="active === 'summary'" v-model="store.content.summary" label="Professional summary" type="textarea" :rows="7" :maxlength="4000" counter
              placeholder="Two or three sentences on who you are, what you do best and what you are looking for." />

            <SectionOrder v-else-if="active === ORDER_KEY" :content="store.content" />

            <DesignSection v-else-if="active === DESIGN_KEY" :settings="store.content.settings" :template="store.resume.template" @template="store.setTemplate" />

            <ListSection v-else-if="LIST_SECTIONS[active]" :key="active" :def="LIST_SECTIONS[active]" :entries="store.content[active]" />

            <template v-else-if="customSection">
              <div class="flex items-end gap-2 mb-4">
                <div class="flex-1"><FormField v-model="customSection.title" label="Section name" placeholder="Awards" :maxlength="80" /></div>
                <button type="button" @click="deletingSection = customSection" class="h-9 px-3 rounded-lg text-[13px] font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">Delete section</button>
              </div>
              <ListSection :key="customSection.id" :def="CUSTOM_SECTION" :entries="customSection.entries" />
            </template>
          </div>
        </div>
      </div>

      <div class="flex-1 min-w-0 overflow-y-auto bg-gray-100 dark:bg-[#0a0d12]" :class="pane === 'preview' ? '' : 'hidden xl:block'">
        <div class="max-w-[820px] mx-auto px-4 sm:px-6 py-5">
          <div class="flex items-center justify-between gap-3 mb-2.5">
            <p class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Preview · A4 · {{ pages }} {{ pages === 1 ? 'page' : 'pages' }}
            </p>
            <!-- Shows the page breaks of the download. Remembered in this browser (useStoredFlag). -->
            <div class="flex items-center gap-2">
              <span id="page-breaks-label" class="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Page breaks</span>
              <button type="button" role="switch" :aria-checked="pageBreaks" aria-labelledby="page-breaks-label" @click="pageBreaks = !pageBreaks"
                class="relative w-8 h-[18px] flex-shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
                :class="pageBreaks ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'">
                <span class="absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full bg-white shadow transition-transform" :class="pageBreaks ? 'translate-x-[14px]' : ''" aria-hidden="true"></span>
              </button>
            </div>
          </div>
          <!-- Pressing the preview opens it big, over the page -->
          <div role="button" tabindex="0" aria-label="Open the preview in full screen" title="Click to see it full screen" @click="zoomed = true"
            @keydown.enter.prevent="zoomed = true" @keydown.space.prevent="zoomed = true"
            class="cursor-zoom-in rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60">
            <ResumePreview :content="store.content" :template="store.resume.template" :picture-url="store.resume.picture_url" :page-breaks="pageBreaks" count-pages @pages="pages = $event" />
          </div>
        </div>
      </div>

      <!-- The resume in full view: 80% of the screen wide, closed with the cross, Escape or a click outside -->
      <Teleport to="body">
        <Transition name="fade">
          <div v-if="zoomed && store.content" class="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm py-6" role="dialog" aria-modal="true"
            aria-label="Resume preview" @click.self="zoomed = false">
            <button type="button" ref="zoomClose" @click="zoomed = false" aria-label="Close the full screen preview" title="Close"
              class="fixed top-4 right-5 z-[101] w-10 h-10 rounded-full inline-flex items-center justify-center bg-white text-gray-700 shadow-lg hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
            </button>
            <div class="w-[80vw] max-w-[1600px] mb-6">
              <ResumePreview :content="store.content" :template="store.resume.template" :picture-url="store.resume.picture_url" :page-breaks="pageBreaks" :max-scale="2.2" />
            </div>
          </div>
        </Transition>
      </Teleport>

      <JsonPanel :open="jsonOpen" @close="jsonOpen = false" />
    </div>

    <ResumePrint v-if="printing && store.content" :content="store.content" :template="store.resume.template" :picture-url="store.resume.picture_url" />

    <ConfirmModal :open="askingSample" title="Fill with sample data?" confirm-text="Fill with sample" :danger="false"
      message="This replaces your personal details, summary, experience, education, skills, projects, certifications and languages with example content. Your look settings, photo and own sections stay. You can undo it right after."
      @cancel="askingSample = false" @confirm="fillSample">
      <template #icon>
        <svg class="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"/></svg>
      </template>
    </ConfirmModal>

    <ConfirmModal :open="Boolean(deletingSection)" title="Delete this section?" confirm-text="Delete section"
      message="The section and everything in it will be removed from this resume."
      @cancel="deletingSection = null" @confirm="removeCustom">
      <template #subject>
        <span v-if="deletingSection" class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ deletingSection.title || 'Untitled section' }}</span>
      </template>
    </ConfirmModal>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useResumeEditorStore } from 'stores/resumeEditor.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import ConfirmModal from 'controllers/vue_components/shared/ConfirmModal.vue'
import SectionRail from './editor/SectionRail.vue'
import SectionOrder from './editor/SectionOrder.vue'
import BasicsSection from './editor/BasicsSection.vue'
import ListSection from './editor/ListSection.vue'
import FormField from './editor/FormField.vue'
import ResumePreview from './editor/ResumePreview.vue'
import ResumeAiInfo from './editor/ResumeAiInfo.vue'
import HelpLink from 'controllers/vue_components/shared/HelpLink.vue'
import ResumePrint from './editor/ResumePrint.vue'
import DesignSection from './editor/DesignSection.vue'
import JsonPanel from './editor/JsonPanel.vue'
import IconButton from 'controllers/vue_components/shared/IconButton.vue'
import { ICONS } from './editor/icons.js'
import { CUSTOM_SECTION, LIST_SECTIONS, SECTION_LABELS } from './editor/sectionDefs.js'
import { DESIGN_KEY, ORDER_KEY } from './editor/sectionOrder.js'
import { useStoredFlag } from 'composables/useStoredFlag.js'

const MAX_CUSTOM = 6 // ResumeContent::MAX_CUSTOM_SECTIONS
const PANES = [{ id: 'edit', label: 'Edit' }, { id: 'preview', label: 'Preview' }]
const STATUS = {
  saved: { text: 'All changes saved', tone: 'text-gray-400 dark:text-gray-500' },
  dirty: { text: 'Unsaved changes', tone: 'text-amber-600 dark:text-amber-400' },
  saving: { text: 'Saving...', tone: 'text-gray-500 dark:text-gray-400' },
  error: { text: 'Not saved', tone: 'text-red-600 dark:text-red-400' },
}

const route = useRoute()
const store = useResumeEditorStore()
const pane = ref('edit')
const pages = ref(1)
// Whether the preview shows the page breaks of the download. Off until chosen, then remembered in this browser, also after a refresh.
const pageBreaks = useStoredFlag('resume_preview_page_breaks', false)
// The preview open full screen. Escape closes it, the page behind does not scroll meanwhile, and focus goes to the cross.
const zoomed = ref(false)
const zoomClose = ref(null)
const onZoomKey = (event) => { if (event.key === 'Escape') zoomed.value = false }
watch(zoomed, async (open) => {
  const method = open ? 'addEventListener' : 'removeEventListener'
  document[method]('keydown', onZoomKey)
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) {
    await nextTick()
    zoomClose.value?.focus()
  }
})
const deletingSection = ref(null)
const flash = ref(null)
const askingSample = ref(false)
const jsonOpen = ref(false)
const printing = ref(false)
const active = ref('basics') // the form showing: basics, summary, a list section, a custom section's id, the section order (ORDER_KEY) or the design (DESIGN_KEY)

const HINTS = {
  basics: 'How an employer reaches you. Only what you fill in is shown.',
  summary: 'A short pitch at the top of the resume.',
  experience: 'Your roles, newest first. Short, measurable achievements read best.',
  education: 'Schools, degrees and courses.',
  skills: 'Group related skills, for example Languages, Frameworks, Tools.',
  projects: 'Work you built, with a link if there is one.',
  certifications: 'Certificates and licences.',
  languages: 'Languages you speak and how well.',
  [ORDER_KEY]: 'Drag the sections into the order you want, and hide the ones you do not need. The preview, the section icons and the JSON follow.',
  [DESIGN_KEY]: 'The template, colour, font and spacing of the whole resume. The preview shows each change.',
}

const customSection = computed(() => store.content?.custom.find((section) => section.id === active.value) || null)
const heading = computed(() => {
  if (active.value === 'basics') return 'Personal details'
  if (active.value === ORDER_KEY) return 'Section order'
  if (active.value === DESIGN_KEY) return 'Design'
  return SECTION_LABELS[active.value] || customSection.value?.title || 'Untitled section'
})

// Every edit changes `content` in place, which this sees; loading a resume replaces it, which
// is not an edit.
watch(() => store.content, (content, before) => {
  if (content && content === before) store.touch()
}, { deep: true })

// The name box shows what the user typed; leaving it empty puts the saved name back.
function restoreName() {
  if (!store.name.trim()) store.setName(store.resume.name)
}

function addCustom(title) {
  active.value = store.addCustomSection(title)
}

function removeCustom() {
  store.removeCustomSection(deletingSection.value.id)
  deletingSection.value = null
  active.value = 'basics'
}

// Sample data fills straight in when there is nothing of the user's to lose, and asks first when there is.
function askSample() {
  if (store.hasOwnContent()) askingSample.value = true
  else fillSample()
}

function fillSample() {
  askingSample.value = false
  store.fillSample()
  active.value = 'basics'
}

// Download PDF: the browser's print dialog, with a page that is only the resume (see
// ResumePrint). The resume's name is the page title for that moment, so it is the file's name.
async function downloadPdf() {
  if (printing.value) return

  printing.value = true
  await nextTick()
  // The picture has to be on the page before it is printed, or the dialog shows a gap.
  await Promise.all([...document.querySelectorAll('.resume-print-root img')].map((image) => image.decode?.().catch(() => {})))
  const paper = document.createElement('style')
  paper.textContent = '@page { size: A4; margin: 0 }'
  const title = document.title
  document.title = store.name || 'Resume'
  document.head.appendChild(paper)
  document.body.classList.add('resume-printing')

  const restore = () => {
    window.removeEventListener('afterprint', restore)
    document.body.classList.remove('resume-printing')
    paper.remove()
    document.title = title
    printing.value = false
  }
  window.addEventListener('afterprint', restore)
  restoreAfterPrint = restore
  window.print()
}
let restoreAfterPrint = null

// Closing the tab with something unsaved asks first.
const warn = (event) => {
  if (!store.unsaved) return
  event.preventDefault()
  event.returnValue = ''
}

onMounted(() => {
  store.load(route.params.id)
  window.addEventListener('beforeunload', warn)
})

watch(() => route.params.id, async (id, was) => {
  if (!id || id === was) return
  active.value = 'basics'
  await store.flush()
  store.load(id)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', warn)
  document.removeEventListener('keydown', onZoomKey)
  document.body.style.overflow = ''
  if (printing.value) restoreAfterPrint?.()
  store.flush()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active { transition: opacity 0.18s ease; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }
</style>
